import "./App.css";
import React, { useEffect, useState } from "react";
import Layout from "./Components/Layout/Layout";
import { BrowserRouter as Router } from "react-router-dom";
import { StoreProvider } from "./Components/StateProvider";
import PageRouter from "./Components/Layout/PageRouter";
import { ToastContainer, toast } from "react-toastify";
import { SignalRProvider, useSignalR } from "./Provider/SignalRProvider";
import {
  initializeOneSignal,
  getNotificationPermissionStatus,
  getServiceWorkerStatus,
  isOneSignalReady,
} from "./services/oneSignalService";
import { isOneSignalConfigValid } from "./config/oneSignalConfig";

function CelebrationOverlay() {
  const { showCelebration } = useSignalR();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (showCelebration) {
      setVisible(true);
    }
  }, [showCelebration]);

  if (!visible) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(45, 43, 43, 0.7)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
        color: "white",
      }}
    >
      <button
        onClick={() => setVisible(false)}
        style={{
          position: "absolute",
          top: "20px",
          right: "30px",
          background: "transparent",
          border: "none",
          fontSize: "58px",
          color: "white",
          cursor: "pointer",
        }}
      >
        &times;
      </button>

      <h3 style={{ marginBottom: "10px", color: "white" }}>
        🎉 Congratulations! You won a badge!
      </h3>
      <img
        src="/Celebration gif.gif"
        alt="You won a badge"
        style={{ width: "250px", borderRadius: "12px" }}
      />
    </div>
  );
}

function App() {
  document.title = "Absolute Legal Law Firm";

  useEffect(() => {
    // Initialize OneSignal on app startup
    const initializeNotifications = async () => {
      if (isOneSignalConfigValid()) {
        // Use appId from config (hardcoded)
        const { appId } = await import("./config/oneSignalConfig").then(m => m.default || m.oneSignalConfig);
        const initOk = await initializeOneSignal(appId);
        console.log("[App] OneSignal initialize returned:", initOk);

        // Log service worker and permission status to help debug localhost
        const swStatus = await getServiceWorkerStatus();
        console.log("[App] ServiceWorker status:", swStatus);
        const perm = await getNotificationPermissionStatus();
        console.log("[App] Notification permission:", perm);
        console.log("[App] isOneSignalReady:", isOneSignalReady());
      } else {
        console.warn("[App] OneSignal App ID not configured");
      }
    };

    initializeNotifications();
  }, []);

  useEffect(() => {
    // Listen for service worker updates and reload when a new SW is installed
    if (!('serviceWorker' in navigator)) return;

    const registerUpdateListeners = async () => {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        registrations.forEach((reg) => {
          // Listen for updatefound on each registration
          reg.addEventListener('updatefound', () => {
            const newSW = reg.installing;
            if (!newSW) return;
            newSW.addEventListener('statechange', () => {
              if (newSW.state === 'installed' && navigator.serviceWorker.controller) {
                // New content available — reload to load the new version
                console.log('[App] New service worker installed, reloading to update');
                window.location.reload();
              }
            });
          });

          // Optionally trigger an update check
          try { reg.update(); } catch (e) { /* ignore update errors */ }
        });
      } catch (err) {
        console.error('[App] Failed to register SW update listeners', err);
      }
    };

    registerUpdateListeners();
  }, []);

  useEffect(() => {
    // Poll meta.json (generated at build) to detect new deployments
    let mounted = true;

    const checkMeta = async () => {
      try {
        const res = await fetch('/meta.json', { cache: 'no-store' });
        if (!res.ok) return;
        const meta = await res.json();
        const current = localStorage.getItem('appVersion');
        if (!current) {
          localStorage.setItem('appVersion', meta.version);
          return;
        }
        if (meta.version !== current) {
          console.log('[App] New version detected:', meta.version, 'old:', current);
          localStorage.setItem('appVersion', meta.version);
          if (mounted) window.location.reload();
        }
      } catch (err) {
        // ignore network errors
      }
    };

    checkMeta();
    const id = setInterval(checkMeta, 60 * 1000); // check every minute
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  useEffect(() => {
    // Compare client meta.json with server /api/version to detect mismatches
    let mounted = true;

    const checkApiVsMeta = async () => {
      try {
        const [metaRes, apiRes] = await Promise.all([
          fetch('/meta.json', { cache: 'no-store' }),
          fetch('/api/version', { cache: 'no-store' })
        ]);

        if (!metaRes.ok || !apiRes.ok) return;

        const meta = await metaRes.json();
        const api = await apiRes.json();
        const apiVersion = api.versionEnv ?? api.versionAssembly ?? null;
        if (!apiVersion || !meta?.version) return;

        if (meta.version !== apiVersion) {
          console.warn('[App] Client build version differs from server version', { client: meta.version, server: apiVersion });
          if (mounted) {
            // Immediately reload to pick up matching server & client versions
            window.location.reload();
          }
        }
      } catch (err) {
        // ignore network/errors
      }
    };

    checkApiVsMeta();
    const id = setInterval(checkApiVsMeta, 60 * 1000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  return (
    <Router>
      <ToastContainer
        position="top-center"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        style={{
          color: "#000",
        }}
      />
      <StoreProvider>
        <SignalRProvider>
          <CelebrationOverlay />
          <PageRouter />
        </SignalRProvider>
      </StoreProvider>
    </Router>
  );
}

export default App;
